import { useRef, useState } from 'react';
import Button from '@/components/ui/Button';
import { compressImage } from '@/utils/image';
import { isIOS } from '@/utils/platform';

interface WineScannerProps {
  onCapture: (file: File) => void;
  label?: string;
}

export default function WineScanner({ onCapture, label = 'Scan label' }: WineScannerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');

  async function handleFileCapture(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    const compressedFile = new File([compressed], file.name, { type: 'image/jpeg' });
    onCapture(compressedFile);
    e.target.value = '';
  }

  async function startLiveCamera() {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setStreaming(true);
      }
    } catch {
      setError('Camera not available. Use the file picker instead.');
    }
  }

  async function captureFrame() {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      stopStream();
      const compressed = await compressImage(new File([blob], 'scan.jpg', { type: 'image/jpeg' }));
      onCapture(new File([compressed], 'scan.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  }

  function stopStream() {
    const stream = videoRef.current?.srcObject as MediaStream | null;
    stream?.getTracks().forEach((t) => t.stop());
    setStreaming(false);
  }

  // iOS Safari: file input is most reliable; live viewfinder optional on other browsers
  const useFileInput = isIOS() || !navigator.mediaDevices?.getUserMedia;

  return (
    <div className="wine-scanner">
      {!streaming && (
        <>
          {!useFileInput && (
            <Button variant="primary" onClick={startLiveCamera} fullWidth>
              Open camera
            </Button>
          )}

          <label className="scanner-file-label">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileCapture}
              className="scanner-file-input"
            />
            <span className="btn btn-secondary btn-md btn-full">
              {useFileInput ? label : 'Pick from photos'}
            </span>
          </label>

          {error && <p className="scanner-error">{error}</p>}
        </>
      )}

      {streaming && (
        <div className="scanner-viewfinder">
          <video ref={videoRef} autoPlay playsInline className="scanner-video" />
          <div className="scanner-frame" aria-hidden />
          <div className="scanner-controls">
            <Button variant="ghost" onClick={stopStream}>Cancel</Button>
            <button className="scanner-shutter" onClick={captureFrame} aria-label="Capture">
              <span className="scanner-shutter-inner" />
            </button>
            <div />
          </div>
        </div>
      )}
    </div>
  );
}
